import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getNotes, addNote, deleteNotes, updateNote , updateComment} from '../../../../services/userService';
import { capitalizeFirstOnChange } from '../../../../utils/textFormat';

function CourseNotes({ contentData, player }) {
  const [notes, setNotes] = useState([]);
  const [showInput, setShowInput] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);

  const moduleId = contentData?.module_id;
  const videoId = contentData?.id;

  useEffect(() => {
    if (videoId) {
      getNotesData();
    }
  }, [moduleId]);

  async function getNotesData() {
    try {
      const response = await getNotes(videoId);
      // console.log('Notes response:', response);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  }

  if (!moduleId || !videoId) {
    return <div className="py-6 text-center text-sm text-font-secondary">No content selected</div>;
  }

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Function to parse time string (mm:ss) to seconds
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    const [minutes, seconds] = timeString.split(':').map(Number);
    return (minutes * 60) + seconds;
  };

  // Function to handle timestamp click and seek to that time
  const handleTimestampClick = (timeString) => {
    if (timeString) {
      try {
        const timeInSeconds = parseTimeToSeconds(timeString);
        
        // Try player prop first
        if (player) {
          if (typeof player.currentTime === 'function') {
            player.currentTime(timeInSeconds);
          } else {
            player.currentTime = timeInSeconds;
          }
        } 
        // Fallback: Use DOM video element
        else {
          const videoElement = document.querySelector('video');
          if (videoElement) {
            videoElement.currentTime = timeInSeconds;
          }
        }
      } catch (error) {
        console.error('Error seeking to timestamp:', error);
      }
    }
  };

  // Function to format date safely
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleSaveNote = async () => {
    if (noteText.trim()) {
      try {
        let currentTime = 0;
        
        // Try multiple ways to get the current time
        if (player) {
          // Method 1: Direct currentTime call
          if (typeof player.currentTime === 'function') {
            currentTime = Math.floor(player.currentTime());
          } 
          // Method 2: currentTime property
          else if (typeof player.currentTime === 'number') {
            currentTime = Math.floor(player.currentTime);
          }
        }
        
        // Fallback: Try to get video element directly from DOM
        if (currentTime === 0) {
          const videoElement = document.querySelector('video');
          if (videoElement && !isNaN(videoElement.currentTime)) {
            currentTime = Math.floor(videoElement.currentTime);
          }
        }
        
        const formattedTime = formatTime(currentTime);
        // console.log('video_id:', videoId, 'note:', noteText, 'stream_time:', formattedTime, 'module_id:', moduleId);

        
        if (editingNoteId) {
          // Handle edit note
          // console.log('formattedTime-------------------------', editingNoteId);
          // console.log('formattedTime-------------------------', noteText);

          const response = await updateNote({
            noteId : editingNoteId,
            note : noteText, 
            // id: editingNoteId,
            // note: noteText,
            // video_id: videoId,
            // module_id: moduleId,
            // stream_time: formattedTime
          });
          
          if (response.success) {
            await getNotesData();
          }
        } else {
          // Add new note
          const response = await addNote({
            note: noteText,
            video_id: videoId,
            module_id: moduleId,
            stream_time: formattedTime
          });
          
          if (response.success) {
            await getNotesData();
          }
        }
        
        setNoteText('');
        setShowInput(true);
        setEditingNoteId(null);
      } catch (error) {
        console.error('Error saving note:', error);
        alert('Failed to save note. Please try again.');
      }
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await deleteNotes(noteId);
      if (response.success) {
        // Immediately remove the note from local state
        setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
        // Show success toast
        toast.success('Note deleted successfully');
        // Then refresh from server to ensure sync
        await getNotesData();
      } else {
        toast.error('Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  return (
    <div>
      {showInput && (
        <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
          <textarea
            className="w-full min-h-[100px] px-4 py-3 rounded-lg border border-gray-200 bg-white text-font-primary placeholder:text-font-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
            placeholder="Make a note..."
            value={noteText}
            onChange={(e) => setNoteText(capitalizeFirstOnChange(e.target.value))}
            autoCapitalize="sentences"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              className="px-4 py-2 rounded-lg font-medium text-font-secondary hover:bg-gray-200 transition"
              onClick={() => {
                setNoteText('');
                setShowInput(true);
                setEditingNoteId(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary/90 transition"
              onClick={handleSaveNote}
            >
              {editingNoteId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {notes.length > 0 ? (
          notes.map((note) => (
            <div key={note.id} className="p-4 rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-2 flex-wrap text-sm text-font-secondary">
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline tabular-nums"
                    onClick={() => handleTimestampClick(note.stream_time)}
                    title="Jump to this time in the video"
                  >
                    {note.stream_time || '0:00'}
                  </button>
                  {formatDate(note.created_at) && <span>{formatDate(note.created_at)}</span>}
                  {note.updated_at && note.updated_at !== note.created_at && formatDate(note.updated_at) && (
                    <span className="text-gray-400">(edited {formatDate(note.updated_at)})</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-2 rounded-lg text-font-secondary hover:bg-gray-100 hover:text-primary transition"
                    onClick={() => {
                      setNoteText(note.note);
                      setEditingNoteId(note.id);
                      setShowInput(true);
                    }}
                    title="Edit note"
                    aria-label="Edit note"
                  >
                    <Icon icon="mdi:pencil" width="18" height="18" />
                  </button>
                  <button
                    type="button"
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"
                    onClick={() => handleDeleteNote(note.id)}
                    title="Delete note"
                    aria-label="Delete note"
                  >
                    <Icon icon="mdi:delete" width="18" height="18" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-font-primary leading-relaxed">{note.note}</p>
            </div>
          ))
        ) : (
          <div className="py-8 text-center text-sm text-font-secondary">
            No notes yet.
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseNotes;
